<script>
  import { createEventDispatcher, onMount } from 'svelte';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faTimes, faSave } from '@fortawesome/pro-light-svg-icons';

  const dispatch = createEventDispatcher();

  export let editType = null;
  export let selectedGroup = null;

  let formData = {
    lookupGroupGUID: '',
    code: '',
    label: '',
    description: '',
    active: true,
    default: false,
    minValue: '',
    maxValue: ''
  };

  let errors = {};

  // Initialize form data when component mounts or editType changes
  onMount(() => {
    initializeFormData();
  });

  $: if (editType !== undefined || selectedGroup !== undefined) {
    initializeFormData();
  }

  function initializeFormData() {
    if (editType) {
      console.log('Initializing form with edit data:', editType);
      formData = {
        _id: editType._id,
        lookupGroupGUID: editType.lookupGroupGUID || (selectedGroup ? selectedGroup._id : ''),
        code: editType.code || '',
        label: editType.label || '',
        description: editType.description || '',
        active: editType.active !== undefined ? editType.active : true,
        default: editType.default !== undefined ? editType.default : false,
        minValue: editType.minValue ?? '',
        maxValue: editType.maxValue ?? ''
      };
    } else if (selectedGroup) {
      // For new types, set the lookup group
      console.log('Initializing form for new type with selected group:', selectedGroup);
      formData = {
        lookupGroupGUID: selectedGroup._id,
        code: '',
        label: '',
        description: '',
        active: true,
        default: false,
        minValue: '',
        maxValue: ''
      };
    } else {
      // Reset form data
      console.log('Initializing form for new type without selected group');
      formData = {
        lookupGroupGUID: '',
        code: '',
        label: '',
        description: '',
        active: true,
        default: false,
        minValue: '',
        maxValue: ''
      };
    }
  }

  // Debug form data changes
  $: console.log('Form data updated:', formData);

  function validateForm() {
    errors = {};

    if (!formData.code.trim()) {
      errors.code = 'Code is required';
    }

    if (!formData.label.trim()) {
      errors.label = 'Label is required';
    }

    // Validate numeric consistency if both provided
    const hasMin = formData.minValue !== '' && formData.minValue !== null;
    const hasMax = formData.maxValue !== '' && formData.maxValue !== null;
    if (hasMin && hasMax) {
      const minNum = parseInt(formData.minValue, 10);
      const maxNum = parseInt(formData.maxValue, 10);
      if (!Number.isInteger(minNum)) {
        errors.minValue = 'minValue must be an integer';
      }
      if (!Number.isInteger(maxNum)) {
        errors.maxValue = 'maxValue must be an integer';
      }
      if (!errors.minValue && !errors.maxValue && minNum > maxNum) {
        errors.maxValue = 'maxValue must be >= minValue';
      }
    }

    return Object.keys(errors).length === 0;
  }

  function handleSubmit() {
    if (!validateForm()) {
      return;
    }

    const submitData = { ...formData };
    // Coerce numbers or nulls for optional numeric fields
    submitData.minValue = submitData.minValue === '' ? null : parseInt(submitData.minValue, 10);
    submitData.maxValue = submitData.maxValue === '' ? null : parseInt(submitData.maxValue, 10);
    
    // Remove _id if it's empty or undefined for new records
    if (!submitData._id) {
      delete submitData._id;
    }

    if (editType) {
      dispatch('update', submitData);
    } else {
      dispatch('create', submitData);
    }
  }

  function handleCancel() {
    dispatch('cancel');
  }
</script>

<div class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" on:click|stopPropagation>
  <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto" on:click|stopPropagation>
    <div class="p-6">
      <div class="flex items-center justify-between mb-6">
        <h2 class="text-xl font-semibold text-gray-900 dark:text-gray-100">
          {editType ? 'Edit Lookup Type' : 'New Lookup Type'}
        </h2>
        <button
          on:click={handleCancel}
          class="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
        >
          <FontAwesomeIcon icon={faTimes} class="h-5 w-5" />
        </button>
      </div>

      <form on:submit|preventDefault={handleSubmit} class="space-y-4" key={editType?._id || 'new'}>
        <!-- Code -->
        <div>
          <label for="code" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Code *
          </label>
          <input
            id="code"
            type="text"
            bind:value={formData.code}
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 {errors.code ? 'border-red-500' : ''}"
            placeholder="Enter unique code"
            autocomplete="off"
          />
          {#if errors.code}
            <p class="mt-1 text-sm text-red-600 dark:text-red-400">{errors.code}</p>
          {/if}
        </div>

        <!-- Label -->
        <div>
          <label for="label" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Label *
          </label>
          <input
            id="label"
            type="text"
            bind:value={formData.label}
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 {errors.label ? 'border-red-500' : ''}"
            placeholder="Enter display label"
            autocomplete="off"
          />
          {#if errors.label}
            <p class="mt-1 text-sm text-red-600 dark:text-red-400">{errors.label}</p>
          {/if}
        </div>

        <!-- Description -->
        <div>
          <label for="description" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Description
          </label>
          <textarea
            id="description"
            bind:value={formData.description}
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
            placeholder="Enter description (optional)"
          ></textarea>
        </div>

        <!-- Min / Max Value (optional) -->
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label for="minValue" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Min Value
            </label>
            <input
              id="minValue"
              type="number"
              inputmode="numeric"
              bind:value={formData.minValue}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 {errors.minValue ? 'border-red-500' : ''}"
              placeholder="Optional"
            />
            {#if errors.minValue}
              <p class="mt-1 text-sm text-red-600 dark:text-red-400">{errors.minValue}</p>
            {/if}
          </div>
          <div>
            <label for="maxValue" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Max Value
            </label>
            <input
              id="maxValue"
              type="number"
              inputmode="numeric"
              bind:value={formData.maxValue}
              class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100 {errors.maxValue ? 'border-red-500' : ''}"
              placeholder="Optional"
            />
            {#if errors.maxValue}
              <p class="mt-1 text-sm text-red-600 dark:text-red-400">{errors.maxValue}</p>
            {/if}
          </div>
        </div>

        <!-- Active Status -->
        <div>
          <label class="flex items-center">
            <input
              type="checkbox"
              bind:checked={formData.active}
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Active</span>
          </label>
        </div>

        <!-- Default Status -->
        <div>
          <label class="flex items-center">
            <input
              type="checkbox"
              bind:checked={formData.default}
              class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <span class="ml-2 text-sm text-gray-700 dark:text-gray-300">Default</span>
          </label>
        </div>

        <!-- Action Buttons -->
        <div class="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            on:click={handleCancel}
            class="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors flex items-center space-x-1"
          >
            <FontAwesomeIcon icon={faSave} class="h-3 w-3" />
            <span>{editType ? 'Update' : 'Create'}</span>
          </button>
        </div>
      </form>
    </div>
  </div>
</div> 